/**
 * Pure edits of the hour formula: add a range (equal shares), remove one
 * (the rest re-share 100), patch a group (sliders re-serialize their
 * expression), move one group's share (the others rebalance — the Flow
 * Optimizer's auto-balance math), and the lookback the config mirrors.
 * For Media Devoted | Created By Ciro Autiero
 */
import {
  DROP_GEN_HOUR_MAX_GROUPS,
  type DropGenHourFormula,
  type DropGenHourFormulaGroup,
  hourRangeKey,
  normalizeWeightsToHundred,
  serializeSliders,
} from '@trafficpulse/shared';

import { rebalance } from '@/features/flow-optimizer/hooks';

const uid = () => Math.random().toString(36).slice(2, 8);

export function newHourGroup(index: number, dateRange: string): DropGenHourFormulaGroup {
  const sliders = { metrics: [{ id: `m-${uid()}`, metric: 'cr', weight: 100 }] };
  return {
    id: `g-${uid()}`,
    name: `Range ${index}`,
    dateRange,
    mode: 'sliders',
    sliders,
    expression: serializeSliders(sliders),
  };
}

export function addHourGroup(f: DropGenHourFormula): DropGenHourFormula {
  if (f.groups.length >= DROP_GEN_HOUR_MAX_GROUPS) return f;
  const groups = [...f.groups, newHourGroup(f.groups.length + 1, 'last-30-days')];
  return { groups, groupWeights: equalShares(groups) };
}

export function removeHourGroup(f: DropGenHourFormula, id: string): DropGenHourFormula {
  if (f.groups.length <= 1) return f;
  const groups = f.groups.filter((g) => g.id !== id);
  const kept = Object.fromEntries(groups.map((g) => [g.id, f.groupWeights[g.id] ?? 0]));
  return { groups, groupWeights: normalizeWeightsToHundred(kept, groups.map((g) => g.id)) };
}

export function patchHourGroup(
  f: DropGenHourFormula,
  id: string,
  patch: Partial<DropGenHourFormulaGroup>,
): DropGenHourFormula {
  return {
    ...f,
    groups: f.groups.map((g) => {
      if (g.id !== id) return g;
      const next = { ...g, ...patch };
      if (next.mode === 'sliders') return { ...next, expression: serializeSliders(next.sliders) };
      return { ...next, expression: next.blocks?.masterExpression ?? next.expression };
    }),
  };
}

/** A range edited by its card (the card keeps its own expression in step). */
export function replaceHourGroup(f: DropGenHourFormula, next: DropGenHourFormulaGroup): DropGenHourFormula {
  return { ...f, groups: f.groups.map((g) => (g.id === next.id ? next : g)) };
}

export function setHourGroupWeight(f: DropGenHourFormula, id: string, weight: number): DropGenHourFormula {
  const current = Object.fromEntries(f.groups.map((g) => [g.id, f.groupWeights[g.id] ?? 0]));
  return { ...f, groupWeights: rebalance(current, { id, target: Math.max(0, Math.min(100, weight)) }) };
}

/** The config's lookbackPreset mirrors the primary range (groups[0]). */
export const lookbackFor = (f: DropGenHourFormula): string => hourRangeKey(f.groups[0]);

function equalShares(groups: DropGenHourFormulaGroup[]): Record<string, number> {
  const ids = groups.map((g) => g.id);
  return normalizeWeightsToHundred(Object.fromEntries(ids.map((id) => [id, 1])), ids);
}
